import { useEffect } from "react";
import PropTypes from "prop-types";
import { Navigate, Outlet } from "react-router";
import toast from "react-hot-toast";
import { useAuthStore } from "../store/authStore";
import { INACTIVITY_MESSAGE } from "../utilities/authMessages.js";
import LoadingSpinner from "./LoadingSpinner";

/**
 * ProtectedRoute component for role-based access control
 * @param {Object} props
 * @param {string[]} [props.requiredRoles] - Array of roles allowed to access the route
 *   - If not provided: public access (anyone can view)
 *   - If provided: only users with one of these roles can access
 */
const ProtectedRoute = ({ requiredRoles }) => {
  const { user, isAuthenticated, isCheckingAuth, sessionExpired } =
    useAuthStore();

  const shouldRedirectToLogin =
    requiredRoles?.length > 0 && (!isAuthenticated || !user);

  // Show toast when redirecting unauthenticated user. Skip when the cause is
  // session expiry — SessionExpiredWatcher owns that toast + redirect instead.
  useEffect(() => {
    if (!isCheckingAuth && shouldRedirectToLogin && !sessionExpired) {
      toast.error("Please log in to access this page.", {
        duration: 4000,
        position: "top-center",
        id: "auth-required", // Prevent duplicate toasts
      });
    }
  }, [isCheckingAuth, shouldRedirectToLogin, sessionExpired]);

  // Show loading spinner while checking authentication status
  if (isCheckingAuth) {
    return <LoadingSpinner />;
  }

  // Scenario 1: Public access - no roles required
  if (!requiredRoles || requiredRoles.length === 0) {
    return <Outlet />;
  }

  // Scenario 2: User not logged in - redirect to login. When the cause is
  // session expiry, carry the same message SessionExpiredWatcher would, so
  // either redirect winning the race produces identical behaviour.
  if (shouldRedirectToLogin) {
    return (
      <Navigate
        to="/auth"
        replace
        state={sessionExpired ? { message: INACTIVITY_MESSAGE } : undefined}
      />
    );
  }

  // Scenario 3: Role check - verify user has required role
  const userRole = user.role || "user";
  const hasRequiredRole = requiredRoles.includes(userRole);

  if (!hasRequiredRole) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and has required role
  return <Outlet />;
};

ProtectedRoute.propTypes = {
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
};

export default ProtectedRoute;
